<template>
  <a-modal
    :visible="visible"
    :title="null"
    :footer="null"
    width="1280px"
    @cancel="handleCancel"
  >
    <div class="dependency-modal">
      <div class="dependency-info">
        <h3>{{ $t("k_b9262cc2") }}</h3>
        <div class="usage-tips">
          <a-alert
            type="info"
            show-icon
            :closable="false"
          >
            <template #message>
              <div class="tips-content">
                <div class="tip-item">
                  {{ $t("k_63ac58d4") }}
                </div>
                <div class="tip-item">
                  <strong>{{ $t("k_94905b98") }}</strong>{{ $t("k_c40b5468") }}
                </div>
              </div>
            </template>
          </a-alert>
        </div>
        <div class="json-editor-container">
          <json-editor
            ref="jsonEditor"
            :value="packageJsonStr"
            :height="'400px'"
            :width="'100%'"
            :tab-size="2"
            @change="handleJsonChange"
          />
        </div>
        <div
          v-if="hasChanges"
          class="action-buttons"
        >
          <a-button @click="handleRestore">
            {{ $t("k_d97f9efc") }}
          </a-button>
          <a-button
            type="primary"
            :loading="isLoading"
            @click="handleUpdate()"
          >
            {{ $t("k_ff2e0c04") }}
          </a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { $t } from '@/i18n';
import dependencyLoader from '@/utils/dependencyLoader';
import { message } from '@/utils/wuji-message';
import JsonEditor from '@components/jsonEditor/index.vue';
import { buildAndUploadDependency } from '@composables/useDepLoader';

const PROGRESS_STAGE = {
  NPM_INSTALL: 'NPM_INSTALL',
  BUNDLE: 'BUNDLE',
  READY: 'READY',
};

export const generateProgressMessage = (progress) => {
  const { stage, success, message } = progress;

  // 处理错误信息
  if (!success && message && Array.isArray(message)) {
    // 从错误信息中提取有意义的部分
    const errorMsg = message.find(msg => msg
      && (msg.includes('notarget')
        || msg.includes('ENOTFOUND')
        || msg.includes('version')
        || msg.includes('package')
        || msg.includes('npm ERR! Failed to fetch')));

    if (errorMsg) {
      // 提取具体的错误信息
      if (errorMsg.includes('No matching version found')) {
        const packageMatch = errorMsg.match(/for (.+?)\.$/);
        if (packageMatch) {
          return $t('k_b6620c6e', { arg_0: packageMatch[1] });
        }
        return $t('k_eb5afed0');
      }
      if (errorMsg.includes('package version that doesn\'t exist')) {
        return $t('k_7eb7e72e');
      }
      if (errorMsg.includes('No versions available for') || errorMsg.includes('npm ERR! Failed to fetch')) {
        const packageMatch = errorMsg.match(/No versions available for (\S+?)/);
        if (packageMatch) {
          return $t('k_c9968070', { arg_0: packageMatch[1] });
        }
        return $t('k_60012c69');
      }
    }
  }

  switch (stage) {
    case PROGRESS_STAGE.NPM_INSTALL:
      if (success) {
        return $t('k_cebc7377');
      }
      return $t('k_a4b5e666');

    case PROGRESS_STAGE.BUNDLE:
      if (success) {
        return $t('k_9a6cb7bf');
      }
      return $t('k_df14598c');

    case PROGRESS_STAGE.READY:
      if (success) {
        return $t('k_af282eed');
      }
      return $t('k_cca46ec5');

    default:
      // 如果有自定义message，使用自定义message
      if (message && Array.isArray(message) && message.length > 0) {
        return message[0];
      }
      if (typeof message === 'string') {
        return message;
      }
      return $t('k_1662ff42');
  }
};

const key = 'dependency-modal-progress';

export const logProgress = (progress) => {
  const wujiMessage = generateProgressMessage(progress);
  const duration = (progress.stage === PROGRESS_STAGE.READY) ? 3 : 0;

  message.closeAll();
  if (progress.success) {
    if (progress.stage !== PROGRESS_STAGE.READY) {
      message.loading({
        content: wujiMessage,
        key,
        duration,
      });
    }
  } else {
    message.error({
      content: wujiMessage,
      key,
      duration: 3,
    });
  }
};


export default {
  name: 'DependencyModal',
  components: {
    JsonEditor,
  },
  model: {
    prop: 'visible',
    event: 'update:visible',
  },
  props: {
    dependencyData: {
      type: Object,
      default: () => ({}),
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPackageJsonStr: '',
      originalPackageJsonStr: '',
      isLoading: false,
    };
  },
  computed: {
    hasChanges() {
      return this.currentPackageJsonStr !== this.originalPackageJsonStr;
    },
    packageJsonStr() {
      if (!this.originalPackageJsonStr) {
        return '{}';
      }
      try {
        const parsedJson = JSON.parse(this.originalPackageJsonStr);
        return JSON.stringify(parsedJson, null, 2);
      } catch (error) {
        console.error('Failed to parse packageJson:', error);
        return this.originalPackageJsonStr;
      }
    },
    projectInfo() {
      return this.$store.state.project?.projectInfo;
    },
  },
  watch: {
    dependencyData: {
      handler(newVal) {
        this.originalPackageJsonStr = newVal.packageJson;
        if (!this.currentPackageJsonStr) {
          this.currentPackageJsonStr = this.originalPackageJsonStr;
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleJsonChange(newValue) {
      this.currentPackageJsonStr = newValue || '{}';
    },

    handleCancel() {
      this.$emit('update:visible', false);
      this.currentPackageJsonStr = this.originalPackageJsonStr;
    },

    handleRestore() {
      this.currentPackageJsonStr = this.originalPackageJsonStr;
      // 重设JSON编辑器的值
      const { jsonEditor } = this.$refs;
      if (jsonEditor) {
        jsonEditor.setEditorValue(this.packageJsonStr);
      }
    },

    async handleUpdate(options = {}) {
      // 防止重复点击
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      try {
        let currentJson = null;
        let originalJson = null;
        try {
          // 验证JSON格式
          currentJson = JSON.parse(this.currentPackageJsonStr || '{}');
          originalJson = JSON.parse(this.originalPackageJsonStr || '{}');
        } catch (error) {
          this.$message.error('JSON格式错误，请检查语法');
          return;
        }

        // 校验是否新增或删除了依赖
        const validationResult = this.validateDependencyChanges(originalJson, currentJson, options);
        if (!validationResult.valid) {
          this.$message.error(validationResult.message);
          return;
        }
        dependencyLoader.onProgress(logProgress);

        const dependencies = Object.keys(currentJson.dependencies || {});
        const { bundle, packageJson, presets } = await buildAndUploadDependency(dependencies, this.projectInfo, this.currentPackageJsonStr);
        this.projectInfo.dependency = {
          ...this.projectInfo.dependency,
          bundle,
          packageJson,
          presets,
        };

        await this.$store.dispatch('updateProjectInfo', { muted: true, ...this.projectInfo });

        message.success('依赖更新成功, 即将刷新预览页...');
        // 更新原始值
        this.originalPackageJsonStr = this.currentPackageJsonStr;
        this.$emit('update-dependency');
      } finally {
        dependencyLoader.offProgress(logProgress);
        this.isLoading = false;
      }
    },
    // 会最终传给gogo
    async manualUpdatePackageJSON(pkgJSON, options) {
      this.currentPackageJsonStr = pkgJSON;
      await this.handleUpdate(options);
    },
    // 会最终传给gogo
    getPackageJSON() {
      return this.packageJsonStr;
    },

    validateDependencyChanges(originalJson, currentJson, { enableAdd, enableDelete, enableWildcard } = { enableAdd: false, enableDelete: false, enableWildcard: false }) {
      // 获取dependencies对象
      const originalDeps = originalJson.dependencies || {};
      const currentDeps = currentJson.dependencies || {};

      // 获取依赖名称列表
      const originalKeys = Object.keys(originalDeps);
      const currentKeys = Object.keys(currentDeps);

      // 检查是否有新增的依赖
      const addedDeps = currentKeys.filter(key => !originalKeys.includes(key));
      if (addedDeps.length > 0 && !enableAdd) {
        return {
          valid: false,
          message: `不允许新增依赖: ${addedDeps.join(', ')}。请在源码中添加引用后重新生成依赖。`,
        };
      }

      // 检查是否有删除的依赖
      const removedDeps = originalKeys.filter(key => !currentKeys.includes(key));
      if (removedDeps.length > 0 && !enableDelete) {
        return {
          valid: false,
          message: `不允许删除依赖: ${removedDeps.join(', ')}。请在源码中删除引用后重新生成依赖。`,
        };
      }

      // 检查版本号是否为固定版本
      if (!enableWildcard) {
        for (const [depName, version] of Object.entries(currentDeps)) {
          if (typeof version === 'string' && /^[~^><]/.test(version)) {
            return {
              valid: false,
              message: `依赖 ${depName} 必须使用固定版本号，当前版本号 ${version} 包含范围符号。`,
            };
          }
        }
      }


      return { valid: true };
    },
  },
};
</script>

<style scoped>
.dependency-modal {
  /* max-height: 600px; */
  overflow-y: auto;
}

.dependency-info {
  margin-bottom: 0;
}

.dependency-info h3 {
  margin-bottom: 16px;
  color: #262626;
  font-size: 16px;
  font-weight: 600;
}

.usage-tips {
  margin-bottom: 16px;
}

.tips-content {
  font-size: 13px;
  line-height: 1.4;
}

.tip-item {
  margin-bottom: 4px;
}

.tip-item:last-child {
  margin-bottom: 0;
}

.json-editor-container {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 0 8px 0;
}
</style>
